import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './readdy.scss';

const ReaddyDetail = () => {
    const navigate = useNavigate();

    // Existing images from portfolio.json
    const galleryImages = [
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.09.42.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.09.49.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.09.57.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.10.05.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.10.16.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 21.10.24.webp',
        '/portfolio/readdy-app/Simulator Screenshot - iPhone 17 Pro Max - 2026-01-12 at 22.39.20.webp'
    ];

    const heroImage = '/portfolio/readdy-app/readdy-app-appStore-screenshot-1.webp';

    return (
        <div className="container readdy-page">
            <button className="back-btn" onClick={() => navigate('/portfolio')}>
                ← Back to Portfolio
            </button>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1>Readdy</h1>
                    <p className="tagline">Reading Habit Tracker for iOS</p>
                    <p className="subtitle">Built for readers who want to build consistent habits without complex tracking</p>

                    <div className="badges">
                        <span className="badge timeline">Built in 6 weeks</span>
                        <span className="badge tech">SwiftUI</span>
                    </div>
                </div>
                <div className="hero-image">
                    <img src={heroImage} alt="Readdy main interface" />
                </div>
            </section>

            {/* The Challenge */}
            <section className="challenge-section">
                <h2>The Challenge</h2>
                <p className="intro">
                    Existing reading apps were too complex—manual time tracking, detailed statistics, overwhelming features. Users wanted one thing: build a daily reading habit without friction.
                </p>
                <p className="ask">
                    <strong>The ask:</strong> Create a reading tracker that's so simple, logging takes one tap. Make it engaging enough to keep users coming back daily.
                </p>
                <div className="requirements">
                    <h3>Key Requirements</h3>
                    <ul>
                        <li>Absolute simplicity (no manual timers or complicated forms)</li>
                        <li>Gamification that motivates without overwhelming</li>
                        <li>Beautiful, approachable UI</li>
                        <li>Works offline (no account required)</li>
                        <li>Fast—from launch to logged in under 3 taps</li>
                    </ul>
                </div>
            </section>

            {/* The Solution */}
            <section className="solution-section">
                <h2>The Solution</h2>
                <p className="intro">
                    Built a native iOS reading habit app with gamification at its core. One-tap logging, streak tracking, and reward badges turn reading into a daily ritual.
                </p>

                <div className="features-grid">
                    <h3>Key Features</h3>
                    <div className="grid">
                        <div className="feature-card">
                            <h4>One-tap logging</h4>
                            <p>Record reading in seconds, no manual timers or lengthy forms</p>
                        </div>
                        <div className="feature-card">
                            <h4>Streak tracking</h4>
                            <p>Daily streak counter with fire emoji motivation and grace period logic</p>
                        </div>
                        <div className="feature-card">
                            <h4>Progress badges</h4>
                            <p>Unlock achievements for milestones (5-day streak, first moment, 30 days, 100 activities)</p>
                        </div>
                        <div className="feature-card">
                            <h4>Reading moments</h4>
                            <p>Log specific passages or quotes you loved with optional notes</p>
                        </div>
                        <div className="feature-card">
                            <h4>Simple summaries</h4>
                            <p>See your reading history at a glance with clean visualizations</p>
                        </div>
                        <div className="feature-card">
                            <h4>Beautiful UI</h4>
                            <p>Orange/coral/cream design system that feels warm and inviting</p>
                        </div>
                        <div className="feature-card">
                            <h4>No barriers</h4>
                            <p>No account creation, no login, no paywalls—just open and use</p>
                        </div>
                    </div>
                </div>

                {/* Screenshot Gallery - Mid Section */}
                <div className="mid-gallery">
                    {galleryImages.slice(0, 3).map((img, idx) => (
                        <div key={idx} className="gallery-item">
                            <img src={img} alt={`Readdy screenshot ${idx + 1}`} />
                        </div>
                    ))}
                </div>

                <div className="technical-approach">
                    <h3>Technical Approach</h3>
                    <div className="grid">
                        <div className="tech-card">
                            <h4>SwiftUI for modern UI</h4>
                            <p>Reactive, responsive interface that feels native</p>
                        </div>
                        <div className="tech-card">
                            <h4>Local persistence</h4>
                            <p>CoreData for storing activities, streaks, and achievements</p>
                        </div>
                        <div className="tech-card">
                            <h4>Custom achievement system</h4>
                            <p>Badge unlocking logic with state management and animations</p>
                        </div>
                        <div className="tech-card">
                            <h4>Smart notifications</h4>
                            <p>Gentle reminders if streak is at risk (optional, user-controlled)</p>
                        </div>
                        <div className="tech-card">
                            <h4>One-handed design</h4>
                            <p>Entire app usable with thumb, optimized for quick logging</p>
                        </div>
                        <div className="tech-card">
                            <h4>Launch-to-log flow</h4>
                            <p>3 taps maximum from cold start to activity logged</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Result */}
            <section className="result-section">
                <h2>The Result</h2>
                <p className="intro">
                    Shipped a reading habit app that users love. Reviews consistently mention how simple it is and how it actually worked to build their reading habit.
                </p>

                <div className="outcomes">
                    <h3>Key Outcomes</h3>
                    <ul>
                        <li><span className="check">✓</span> High App Store rating – Users report building reading habits within first week</li>
                        <li><span className="check">✓</span> High retention – Streak system drives daily opens and engagement</li>
                        <li><span className="check">✓</span> Clean UX – Non-readers find it approachable, avid readers love the simplicity</li>
                        <li><span className="check">✓</span> Polished UI – Feels premium, not like an indie side project</li>
                    </ul>
                </div>

                <div className="testimonials">
                    <h3>User Impact</h3>
                    <div className="quotes">
                        <blockquote>"Finally a reading tracker that doesn't feel like homework. I actually use this every day."</blockquote>
                        <blockquote>"Love the simplicity. One tap and I'm done. The streaks keep me motivated."</blockquote>
                        <blockquote>"The badges are silly but they work—I don't want to lose my streak!"</blockquote>
                    </div>
                </div>

                {/* More Screenshots */}
                <div className="mid-gallery">
                    {galleryImages.slice(3, 5).map((img, idx) => (
                        <div key={idx} className="gallery-item">
                            <img src={img} alt={`Readdy screenshot ${idx + 4}`} />
                        </div>
                    ))}
                </div>
            </section>

            {/* What This Proves */}
            <section className="proves-section">
                <h2>What This Proves</h2>
                <p className="headline">We can build habit-forming consumer apps.</p>
                <p>If your startup needs:</p>
                <ul>
                    <li>Gamification and engagement loops that actually work</li>
                    <li>Simple, focused UX (do one thing extremely well)</li>
                    <li>Habit tracking and streak systems</li>
                    <li>Beautiful, polished UI that feels premium</li>
                    <li>Apps that people actually open and use daily</li>
                </ul>
                <p className="emphasis">...we've done it in production.</p>

                <div className="why-matters">
                    <h3>Why This Matters</h3>
                    <p>Most habit apps fail because they're too complicated or feel like chores. Readdy succeeds because it removes every possible point of friction and makes logging feel rewarding, not burdensome.</p>
                    <p>The same principles apply to fitness apps, health tracking, productivity tools—anywhere you need users to build a daily habit.</p>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="tech-stack-section">
                <h2>Tech Stack</h2>
                <div className="stack-table">
                    <div className="stack-row">
                        <span className="label">Language</span>
                        <span className="value">Swift</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">UI Framework</span>
                        <span className="value">SwiftUI</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Data Persistence</span>
                        <span className="value">CoreData (local storage, no backend)</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Notifications</span>
                        <span className="value">UserNotifications framework for optional streak reminders</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Architecture</span>
                        <span className="value">MVVM pattern, clean data flow with Combine</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Design System</span>
                        <span className="value">Custom color tokens, typography scale, consistent spacing</span>
                    </div>
                    <div className="stack-row">
                        <span className="label">Minimum Requirements</span>
                        <span className="value">iOS 16+, iPhone SE and newer</span>
                    </div>
                </div>
            </section>

            {/* Design Highlights */}
            <section className="design-section">
                <h2>Design Highlights</h2>

                <div className="design-grid">
                    <div className="design-card">
                        <h3>Color Psychology</h3>
                        <p>Orange/coral palette chosen for warmth and motivation:</p>
                        <ul>
                            <li>Orange = energy, enthusiasm, creativity</li>
                            <li>Cream = calm, approachable, not sterile</li>
                            <li>Dark mode with warm tones (not pure black)</li>
                        </ul>
                    </div>

                    <div className="design-card">
                        <h3>Gamification That Works</h3>
                        <ul>
                            <li>Streaks with grace period (miss 1 day = warning, 2 = reset)</li>
                            <li>First activity badge: everyone wins immediately</li>
                            <li>Short streaks (5, 10 days) for early motivation</li>
                            <li>Long streaks (30, 100 days) for sustained engagement</li>
                            <li>No XP or complex points—just streaks and badges</li>
                        </ul>
                    </div>

                    <div className="design-card">
                        <h3>Accessibility</h3>
                        <ul>
                            <li>Full VoiceOver support (log activities hands-free)</li>
                            <li>Dynamic Type (scales with system text size)</li>
                            <li>High contrast mode for low vision users</li>
                            <li>Haptic feedback for key interactions</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Gamification Breakdown */}
            <section className="gamification-section">
                <h2>Gamification Breakdown</h2>

                <div className="gamification-grid">
                    <div className="gamification-card">
                        <h3>Streak System Logic</h3>
                        <div className="streak-info">
                            <p><strong>Consecutive days logged:</strong></p>
                            <ul>
                                <li>Day 1-4: Building momentum</li>
                                <li>Day 5: First milestone badge unlocked</li>
                                <li>Day 10: Second milestone badge</li>
                                <li>Day 30: Major achievement badge</li>
                                <li>Day 100+: Elite status</li>
                            </ul>
                            <p><strong>Grace period:</strong></p>
                            <ul>
                                <li>Miss 1 day: Warning notification sent</li>
                                <li>Miss 2+ days: Streak resets to 0</li>
                            </ul>
                        </div>
                    </div>

                    <div className="gamification-card badges-card">
                        <h3>Badge Progression</h3>
                        <div className="badge-groups">
                            <div className="badge-group">
                                <h4>Starter Badges</h4>
                                <ul>
                                    <li><span className="badge-icon">◎</span> First Activity – Log your first reading session</li>
                                    <li><span className="badge-icon">★</span> 5-Day Streak – Read 5 days in a row</li>
                                    <li><span className="badge-icon">◆</span> 25 Activities – Log 25 total reading sessions</li>
                                </ul>
                            </div>
                            <div className="badge-group">
                                <h4>Intermediate Badges</h4>
                                <ul>
                                    <li><span className="badge-icon">⚡</span> 10-Day Streak</li>
                                    <li><span className="badge-icon">▣</span> 50 Activities</li>
                                    <li><span className="badge-icon">◇</span> First Reading Moment</li>
                                </ul>
                            </div>
                            <div className="badge-group">
                                <h4>Advanced Badges</h4>
                                <ul>
                                    <li><span className="badge-icon">☆</span> 30-Day Streak</li>
                                    <li><span className="badge-icon">▧</span> 100 Activities</li>
                                    <li><span className="badge-icon">♛</span> Legend Status (hidden criteria)</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="why-works">
                    <h3>Why This Works</h3>
                    <p>Research shows:</p>
                    <ul>
                        <li>Streaks increase retention by 3-5x</li>
                        <li>Visible progress bars drive completion</li>
                        <li>Badges trigger dopamine (even when "silly")</li>
                        <li>Grace periods prevent one-miss demotivation</li>
                    </ul>
                    <p className="callout">Readdy users repeatedly cite "not wanting to break my streak" as their primary motivation to read daily.</p>
                </div>
            </section>

            {/* Project Timeline */}
            <section className="timeline-section">
                <h2>Project Scope & Timeline</h2>
                <div className="timeline-info">
                    <p><strong>Duration:</strong> 6 weeks from kickoff to App Store launch</p>
                    <p><strong>Team:</strong> Solo developer (me)</p>
                </div>

                <div className="deliverables">
                    <h3>Deliverables</h3>
                    <ul>
                        <li>Native iOS app (Swift/SwiftUI)</li>
                        <li>Habit logging system</li>
                        <li>Streak tracking with grace period</li>
                        <li>8 achievement badges</li>
                        <li>Reading moments feature</li>
                        <li>Push notifications (optional)</li>
                        <li>App Store submission</li>
                        <li>2 weeks post-launch support</li>
                    </ul>
                </div>

                <div className="week-breakdown">
                    <div className="week">
                        <span className="week-label">Week 1</span>
                        <span className="week-task">Design, wireframes, color system</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 2-3</span>
                        <span className="week-task">Core logging and streak system</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 4</span>
                        <span className="week-task">Achievement system and notifications</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 5</span>
                        <span className="week-task">Polish, animations, accessibility</span>
                    </div>
                    <div className="week">
                        <span className="week-label">Week 6</span>
                        <span className="week-task">Testing, App Store prep, launch</span>
                    </div>
                </div>
            </section>

            {/* Lessons Learned */}
            <section className="lessons-section">
                <h2>Lessons Learned</h2>

                <div className="lessons-grid">
                    <div className="lessons-card worked">
                        <h3>What Worked</h3>
                        <ul>
                            <li><strong>Ruthless simplicity:</strong> Cutting features was hard, but made the app better. Every removed feature made core experience stronger.</li>
                            <li><strong>Streaks over stats:</strong> Users don't care about "total pages read" or "reading velocity"—they care about consistency.</li>
                            <li><strong>Fast onboarding:</strong> No tutorial needed. Users understand app in 10 seconds.</li>
                            <li><strong>Warm design:</strong> Orange/cream felt more inviting than typical productivity app blue/gray.</li>
                        </ul>
                    </div>

                    <div className="lessons-card different">
                        <h3>What We'd Do Differently</h3>
                        <ul>
                            <li><strong>Social features:</strong> Some users asked for sharing streaks with friends. Might add in v2.</li>
                            <li><strong>Reading moments:</strong> Feature is underused—could be more prominent.</li>
                            <li><strong>iPad optimization:</strong> Works on iPad but wasn't optimized. Would prioritize for launch next time.</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <h2>Want Something Similar?</h2>
                <p>If you're building a health, fitness, or habit-tracking iOS app and need:</p>
                <ul>
                    <li>Gamification systems that drive daily engagement</li>
                    <li>Simple, beautiful UX that anyone can understand</li>
                    <li>Habit tracking and streak mechanics</li>
                    <li>Apps users actually open every day</li>
                    <li>Polished UI that feels premium</li>
                </ul>
                <p className="pricing-note">We can help. Fixed-price packages starting at $15k, 4–8 week timelines.</p>

                <div className="relevant-for">
                    <h3>Relevant For:</h3>
                    <div className="use-cases">
                        <div className="use-case">
                            <h4>Fitness Apps</h4>
                            <p>Workout logging, activity tracking, exercise streaks</p>
                        </div>
                        <div className="use-case">
                            <h4>Health Apps</h4>
                            <p>Medication reminders, symptom tracking, wellness habits</p>
                        </div>
                        <div className="use-case">
                            <h4>Productivity Apps</h4>
                            <p>Daily journaling, goal tracking, routine building</p>
                        </div>
                        <div className="use-case">
                            <h4>Learning Apps</h4>
                            <p>Language practice, skill development, study habits</p>
                        </div>
                    </div>
                </div>

                <div className="cta-buttons">
                    <Link to="/pricing" className="flat-button primary">
                        View iOS Packages & Pricing →
                    </Link>
                    <a
                        href="https://calendly.com/vermresearch-proton/30min"
                        target="_blank"
                        rel="noreferrer"
                        className="flat-button secondary"
                    >
                        Schedule Free 20-Min Call
                    </a>
                </div>
            </section>

            {/* Related Work */}
            <section className="related-section">
                <h3>Related Work</h3>
                <p>Also check out: <Link to="/portfolio/my-advisor">My-Advisor</Link> – Private AI chat app with on-device Core ML</p>
            </section>

            {/* Footer Meta */}
            <section className="meta-section">
                <div className="meta-tags">
                    <span><strong>Project Type:</strong> iOS App Development, Consumer App, Gamification</span>
                    <span><strong>Industry:</strong> EdTech, Productivity, Habit Formation</span>
                    <span><strong>Timeline:</strong> 6 weeks</span>
                    <span><strong>Technologies:</strong> Swift, SwiftUI, CoreData, UserNotifications</span>
                    <span><strong>Platform:</strong> iOS 16+</span>
                </div>
            </section>
        </div>
    );
};

export default ReaddyDetail;
